/**
 * features/statements/statementData.ts
 * Builds a client account ledger from issued invoices (debits),
 * payments (credits) and issued credit notes (credits), with a
 * running balance, opening/closing figures for the chosen period.
 */
import type { Invoice, Payment, CreditNote } from '@/types';

export interface StatementEntry {
  date: string;
  ref: string;
  description: string;
  debit: number;
  credit: number;
  balance: number;
}

export interface StatementResult {
  opening: number;
  entries: StatementEntry[];
  closing: number;
  totalDebit: number;
  totalCredit: number;
}

interface Raw {
  date: string;
  ref: string;
  description: string;
  debit: number;
  credit: number;
}

const METHOD_LABEL: Record<string, string> = {
  bank_transfer: 'Bank transfer',
  cash: 'Cash',
  cheque: 'Cheque',
  card: 'Card',
  mobile: 'Mobile money',
  other: 'Payment',
};

export function buildStatement(
  clientId: string,
  invoices: Invoice[],
  payments: Payment[],
  creditNotes: CreditNote[],
  from?: string,
  to?: string,
): StatementResult {
  const raw: Raw[] = [];

  invoices
    .filter((i) => i.clientId === clientId && i.number && i.status !== 'draft' && i.status !== 'void')
    .forEach((i) =>
      raw.push({ date: i.issueDate, ref: i.number!, description: 'Invoice', debit: i.totals.total, credit: 0 }),
    );

  payments
    .filter((p) => p.clientId === clientId && !p.reversed)
    .forEach((p) =>
      raw.push({
        date: p.date,
        ref: p.receiptNumber,
        description: METHOD_LABEL[p.method] ?? 'Payment',
        debit: 0,
        credit: p.amount,
      }),
    );

  creditNotes
    .filter((c) => c.clientId === clientId && c.number && c.status === 'issued')
    .forEach((c) =>
      raw.push({ date: c.issueDate, ref: c.number!, description: 'Credit note', debit: 0, credit: c.totals.total }),
    );

  raw.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  let opening = 0;
  const entries: StatementEntry[] = [];
  let running = 0;
  let totalDebit = 0;
  let totalCredit = 0;

  for (const r of raw) {
    if (from && r.date < from) {
      opening += r.debit - r.credit;
      continue;
    }
    if (to && r.date > to) continue;
    running = (entries.length ? running : opening) + r.debit - r.credit;
    totalDebit += r.debit;
    totalCredit += r.credit;
    entries.push({ ...r, balance: running });
  }

  const closing = entries.length ? running : opening;
  return { opening, entries, closing, totalDebit, totalCredit };
}
